import { Component, OnInit } from '@angular/core';
import { SelectItem } from 'primeng/api';
import { ItemVenda, Venda, VendaSituacao } from 'src/app/core/models/venda';
import { ProdutoService } from 'src/app/core/services/produto.service';
import { ToastService } from 'src/app/core/services/toast.service';
import { VendaService } from 'src/app/core/services/venda.service';
import { Vendedor } from 'src/app/core/models/vendedor';
import { VendedorService } from 'src/app/core/services/vendedor.service';
import { AuthService } from 'src/app/core/services/auth.service';
import { UsuarioService } from 'src/app/core/services/usuario.service';
import { firstValueFrom } from 'rxjs';
import { Produto } from 'src/app/core/models/produto';

@Component({
  selector: 'app-venda',
  templateUrl: './venda.component.html',
  styleUrls: ['./venda.component.scss'],
})
export class VendaComponent implements OnInit {
  venda: Venda = new Venda();
  produto: Produto | null = null;
  produtos: Produto[] = [];
  itemVenda: ItemVenda = new ItemVenda();

  selectedProduct: any = {};
  showModalPagamento: boolean = false;

  vendedores: Vendedor[] = [];
  selectedVendedor: Vendedor | null = null;
  loggedUserId: number | null = null;
  optionsProducts: SelectItem[] = [];

  // Opções para o status da venda na ordem desejada
  statusOptions = [
    { label: 'Venda', value: VendaSituacao.venda },
    { label: 'Orçamento', value: VendaSituacao.orcamento },
    { label: 'Pedido', value: VendaSituacao.pedido },
  ];

  constructor(
    private readonly produtoService: ProdutoService,
    private readonly vendaService: VendaService,
    private readonly toastService: ToastService,
    private readonly vendedorService: VendedorService,
    private readonly authService: AuthService,
    private readonly usuarioService: UsuarioService
  ) {}

  ngOnInit() {
    // Define o status inicial da venda como o primeiro da lista
    this.venda.vendaSituacao = this.statusOptions[0].value;

    // Carrega vendedores e seleciona automaticamente o vendedor do usuário logado
    this.vendedorService.selecionar().subscribe({
      next: (retorno) => {
        this.vendedores = retorno;
        // Seleciona automaticamente o vendedor do usuário logado
        const currentUser = this.authService.getCurrentUser();
        this.loggedUserId = this.resolveLoggedUserId(currentUser);
        this.setLoggedUserVendedor();
      },
      error: (err) => {
        console.error('Erro ao carregar vendedores:', err);
        this.vendedores = [];
      },
    });

    // Carrega produtos para o dropdown
    this.produtoService.selecionar().subscribe({
      next: (produtos) => {
        this.produtos = produtos;
      },
      error: (err) => {
        console.error('Erro ao carregar produtos:', err);
        this.produtos = [];
      }
    });

    // Atualiza loggedUserId ao mudar o usuário logado
    this.authService.currentUser$.subscribe((user) => {
      this.loggedUserId = this.resolveLoggedUserId(user);
      this.setLoggedUserVendedor();
    });
  }

  private resolveLoggedUserId(user: any): number | null {
    if (!user) return null;

    const candidates = [user.id, user.userId, user.usuarioId, user.usuario?.id];
    for (const candidate of candidates) {
      const parsed = Number(candidate);
      if (Number.isInteger(parsed) && parsed > 0) {
        return parsed;
      }
    }

    return null;
  }

  private getCurrentUsername(user: any): string | null {
    const raw = user?.username || user?.sub || user?.login || user?.usuario?.username;
    if (!raw) return null;
    return String(raw).trim() || null;
  }

  private async ensureLoggedUserId(): Promise<number | null> {
    if (this.loggedUserId && this.loggedUserId > 0) {
      return this.loggedUserId;
    }

    const currentUser = this.authService.getCurrentUser();
    const fromClaims = this.resolveLoggedUserId(currentUser);
    if (fromClaims) {
      this.loggedUserId = fromClaims;
      return fromClaims;
    }

    const username = this.getCurrentUsername(currentUser);
    if (!username) {
      return null;
    }

    try {
      const usuarios = await firstValueFrom(this.usuarioService.selecionar());
      const match = usuarios.find((u) => (u.username || '').toLowerCase() === username.toLowerCase());
      const resolvedId = Number(match?.id);
      if (Number.isInteger(resolvedId) && resolvedId > 0) {
        this.loggedUserId = resolvedId;
        return resolvedId;
      }
    } catch (error) {
      console.error('Erro ao resolver usuario logado:', error);
    }

    return null;
  }

  searchProduct(event: any) {
    const search = event.query;

    this.produtoService.pesquisar(search).subscribe({
      next: (produtos) => {
        this.optionsProducts = produtos.map((p) => ({
          label: p.nome,
          value: p,
        }));
      },
      error: (error: any) => {
        console.error('Erro ao buscar produtos:', error);
        this.optionsProducts = [];
        let errorMessage = 'Erro ao buscar produtos. Tente novamente.';

        if (error?.message) {
          errorMessage = error.message;
        } else if (typeof error?.error === 'string') {
          errorMessage = error.error;
        }

        this.toastService.error(errorMessage);
      }
    });
  }
  
  addProduct() {
    const selectedProduct = this.selectedProduct.value;
    if (selectedProduct && this.itemVenda.quantidade > 0) {
      this.itemVenda.produto = selectedProduct;
      this.venda.addItemVenda(this.itemVenda);

      this.itemVenda = new ItemVenda();
      this.selectedProduct = {};
    }
  }

  removerItem(index: number) {
    this.venda.removerItemVenda(index);
    this.toastService.success('Produto removido com sucesso!');
  }

  finalizeSale() {
    this.showModalPagamento = true;
  }

  async confirmSale(updatedVendaFromModal: Venda) {
    if (updatedVendaFromModal) {
      this.venda = updatedVendaFromModal;
    }

    if (!this.selectedVendedor) {
      this.toastService.error('Selecione um vendedor para finalizar a venda.');
      return;
    }

    this.venda.vendedor = this.selectedVendedor;

    const userId = await this.ensureLoggedUserId();
    if (!userId) {
      this.toastService.error('Nao foi possivel identificar o usuario logado para finalizar a venda.');
      return;
    }

    this.venda.usuario = {
      ...(this.venda.usuario || {}),
      id: userId,
    };

    if (typeof (this.venda as any).recalculateTotals === 'function') {
      (this.venda as any).recalculateTotals();
    }

    console.log('[VendaComponent] Enviando venda:', this.venda);

    this.vendaService.cadastrar(this.venda).subscribe({
      next: () => {
        this.clearData();
        this.toastService.success('Venda efetuada com sucesso!');
      },
      error: (error: any) => {
        console.error('Erro ao finalizar venda:', error);
        let errorMessage = 'Erro ao finalizar venda. Tente novamente.';
        if (error?.message) {
          errorMessage = error.message;
        } else if (error?.error?.message) {
          errorMessage = error.error.message;
        } else if (typeof error?.error === 'string') {
          errorMessage = error.error;
        }

        this.toastService.error(errorMessage);
      }
    });
  }

  cancelSale() {
    this.clearData();
  }

  clearData() {
    this.venda = new Venda();
    this.itemVenda = new ItemVenda();
    this.selectedProduct = {};
    this.showModalPagamento = false;
    this.selectedVendedor = null;
    // Garante que o status padrão seja 'Venda Fechada' ao limpar
    this.venda.vendaSituacao = this.statusOptions[0].value;
    // Re-seleciona o vendedor do usuário logado após limpar
    this.setLoggedUserVendedor();
  }

  private setLoggedUserVendedor() {
    if (this.loggedUserId && this.vendedores.length > 0) {
      const vendedorLogado = this.vendedores.find(v => v.usuarioId === this.loggedUserId || v.usuario?.id === this.loggedUserId);
      if (vendedorLogado) {
        this.selectedVendedor = vendedorLogado;
      }
    }
  }
}
